import React from 'react';
import 'fragments/landing/landing.scss';
import { LandingFragmentProps } from 'fragments/landing/interfaces';
import { useLandingController } from 'fragments/landing/landing.controller';
import { Link } from 'react-router-dom';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import logoSrc from 'assets/img/logo-administra-tus-propiedades.svg';
import heroMockup from 'assets/img/hero-mockup-transparent.png';
import audienceImage from 'assets/img/audience-blob.jpg';
import communityImage from 'assets/img/community-blob.jpg';
import { ReactComponent as IconContratos } from 'assets/icons-svg/icons-navegaci-n-contratos.svg';
import { ReactComponent as IconHome } from 'assets/icons-svg/icons-navegaci-n-home.svg';
import { ReactComponent as IconRecibos } from 'assets/icons-svg/icons-recibo-libre.svg';
import { ReactComponent as IconNotificaciones } from 'assets/icons-svg/icons-navegaci-n-notificaciones.svg';

const FEATURES = [
    {
        icon: IconContratos,
        title: 'Contratos\norganizados',
        description: '',
    },
    {
        icon: IconHome,
        title: 'Seguimiento de\nalquileres',
        description: '',
    },
    {
        icon: IconRecibos,
        title: 'Recibos y\nliquidaciones',
        description: '',
    },
    {
        icon: IconNotificaciones,
        title: 'Notificaciones\nautomáticas',
        description: '',
    },
];

export const LandingFragment: React.FC<LandingFragmentProps> = (props) => {
    const { useController = useLandingController } = props;
    useController();
    const { translate } = useTranslator();

    return (
        <div className="landing">
            {/* ── Header ── */}
            <header className="landing__header">
                <div className="landing__container landing__header-inner">
                    <img src={logoSrc} alt="Administrá tus propiedades" className="landing__logo" />
                    <nav className="landing__nav">
                        <Link to="/login" className="landing__nav-login">
                            Iniciar sesión
                        </Link>
                        <Link to="/register" className="landing__cta landing__cta--primary landing__cta--header">
                            {translate({ key: 'landing.header-cta' })}
                        </Link>
                    </nav>
                </div>
            </header>

            {/* ── Hero ── */}
            <section className="landing__hero">
                <div className="landing__container landing__hero-grid">
                    <div className="landing__hero-text">
                        <h1 className="landing__hero-title">
                            Administrá tus <br />
                            propiedades <br />
                            sin costo.
                        </h1>
                        <p className="landing__free-note">Es 100% gratuito.</p>
                        <div className="landing__actions">
                            <Link to="/register" className="landing__cta landing__cta--primary">
                                Crear una cuenta gratis
                            </Link>
                            <Link to="/login" className="landing__cta landing__cta--secondary">
                                Ya tengo cuenta
                            </Link>
                        </div>
                    </div>
                    <div className="landing__hero-media">
                        <img src={heroMockup} alt="ATP Platform Preview" className="landing__hero-image" />
                    </div>
                </div>
            </section>

            {/* ── Para quién es ── */}
            <section className="landing__audience">
                <div className="landing__container landing__audience-grid">
                    <div className="landing__audience-media">
                        <img src={audienceImage} alt="Para quién es" className="landing__audience-image" />
                    </div>
                    <div className="landing__audience-content">
                        <h2 className="landing__section-title">¿Para quién es Administrá tus propiedades?</h2>
                        <div className="landing__audience-cards">
                            <div className="landing__audience-card">
                                <div className="landing__audience-badge">Particulares</div>
                                <ul className="landing__audience-list">
                                    <li>
                                        <span className="check">✓</span>Gestioná uno o varios alquileres sin Excel ni
                                        papeles.
                                    </li>
                                    <li>
                                        <span className="check">✓</span>Controlá pagos, vencimientos y todo en un solo
                                        lugar.
                                    </li>
                                </ul>
                            </div>
                            <div className="landing__audience-card">
                                <div className="landing__audience-badge">Inmobiliarias</div>
                                <ul className="landing__audience-list">
                                    <li>
                                        <span className="check">✓</span>Administrá múltiples propiedades y propietarios
                                        de forma ordenada y escalable.
                                    </li>
                                    <li>
                                        <span className="check">✓</span>Toda la gestión centralizada y accesible online.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Features ── */}
            <section className="landing__features">
                <div className="landing__container">
                    <h2 className="landing__section-title landing__features-title">
                        Todo lo que necesitás para <br className="desktop-break" />
                        gestionar alquileres...
                    </h2>
                    <div className="landing__features-grid">
                        {FEATURES.map((feature) => (
                            <div key={feature.title} className="landing__feature-card">
                                <div className="landing__feature-icon-circle">
                                    <feature.icon className="landing__feature-svg" />
                                </div>
                                <h3 className="landing__feature-title">
                                    {feature.title.split('\n').map((line, i) => (
                                        <span key={i}>
                                            {line} <br />
                                        </span>
                                    ))}
                                </h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Somos mas que una app ── */}
            <section className="landing__why-free">
                <div className="landing__container landing__why-free-grid">
                    <div className="landing__why-free-text">
                        <h2 className="landing__section-title">
                            Somos más <br className="desktop-break" /> que una app...
                        </h2>
                        <p className="landing__why-free-text-bold">
                            Es una herramienta simple para gestionar propiedades día a día. Más que una app, evoluciona
                            con quienes la usan.
                        </p>
                        <p className="landing__why-free-text-light">
                            Hoy la plataforma es completamente gratuita. En el futuro quizás ofrezcamos servicios o
                            herramientas opcionales, pero la decisión de usarlos siempre va a ser tuya. La base de la
                            app (contratos, pagos, recibos, notificaciones) no va a tener costo.
                        </p>
                        <div className="landing__why-free-items">
                            <div className="landing__why-free-item">
                                <span className="check">✓</span> Sin tarjeta de crédito
                            </div>
                            <div className="landing__why-free-item">
                                <span className="check">✓</span> Sin período de prueba
                            </div>
                            <div className="landing__why-free-item">
                                <span className="check">✓</span> Sin contratos atados
                            </div>
                        </div>
                    </div>
                    <div className="landing__why-free-media">
                        <img src={communityImage} alt="Comunidad" className="landing__why-free-image" />
                    </div>
                </div>
            </section>

            {/* ── Cierre ── */}
            <section className="landing__closing">
                <div className="landing__container">
                    <h2 className="landing__closing-title">Administrá tus propiedades sin costo.</h2>
                    <p className="landing__free-note">Es 100% gratuito.</p>
                    <div className="landing__actions">
                        <Link to="/register" className="landing__cta landing__cta--primary">
                            Crear una cuenta gratis
                        </Link>
                        <Link to="/login" className="landing__cta landing__cta--secondary">
                            Ya tengo cuenta
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="landing__footer">
                <div className="landing__container">
                    <div className="landing__footer-content">
                        <div className="landing__footer-top">
                            <img src={logoSrc} alt="Administrá tus propiedades" className="landing__footer-logo" />
                            <p className="landing__footer-description">
                                Simplificá la gestión de tus alquileres sin costo alguno.
                            </p>
                        </div>

                        <div className="landing__footer-right-column">
                            <div className="landing__footer-links-minimal">
                                <a href="#">Términos y Condiciones</a>
                                <a href="#">Políticas de Privacidad</a>
                                <a href="#">Contacto</a>
                            </div>
                            <div className="landing__footer-social">
                                <span>Seguinos!</span>
                                <a
                                    href="https://www.instagram.com/administratuspropiedades/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <svg viewBox="0 0 24 24">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                                    </svg>
                                </a>
                                <a
                                    href="https://www.facebook.com/admprop.tech"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <svg viewBox="0 0 24 24">
                                        <path d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.593 1.323-1.325V1.325C24 .593 23.407 0 22.675 0z" />
                                    </svg>
                                </a>
                                <a
                                    href="https://www.linkedin.com/company/administratuspropiedades/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <svg viewBox="0 0 24 24">
                                        <path d="M22.23 0H1.77C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.77 24h20.46c.978 0 1.77-.773 1.77-1.729V1.729C24 .774 23.208 0 22.23 0zM7.12 20.452H3.558V9h3.562v11.452zM5.339 7.433a2.063 2.063 0 11.002-4.126 2.063 2.063 0 01-.002 4.126zM20.452 20.452h-3.56v-5.57c0-1.328-.025-3.036-1.85-3.036-1.85 0-2.133 1.445-2.133 2.94v5.666H9.35V9h3.414v1.566h.048c.475-.9 1.636-1.85 3.367-1.85 3.604 0 4.27 2.371 4.27 5.454v6.282z" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="landing__footer-bottom">
                        <p className="landing__footer-copyright">
                            © {new Date().getFullYear()} AdminProp. Todos los derechos reservados.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};
